import { ArrowRight, ShoppingBag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CartLineItemProduct, CheckoutCartData } from '@/Domain/api/Conversation/dto/Message.dto';
import { PayPoLogo } from '@/Domain/components/Inbox/Message/PayPoLogo';

type Props = {
  data: CheckoutCartData;
};

const formatCurrency = (amount: string, currency: string): string => {
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(num);
};

const LineItem = ({ item, currency }: { item: CartLineItemProduct; currency: string }) => (
  <div className="flex flex-col gap-0.5 px-4 py-2.5">
    <span className="font-medium text-sm text-foreground leading-tight">{item.title}</span>
    <div className="flex items-center justify-between">
      <span className="text-xs text-foreground-subtle">
        {item.variant_title && `${item.variant_title} / `}
        {item.quantity} &times; {formatCurrency(item.unit_price, currency)}
      </span>
    </div>
  </div>
);

export const InboxCheckoutCart = ({ data }: Props) => {
  const { checkout_url, checkout_label, is_paypo, currency, total_amount, line_items } = data;
  const { t } = useTranslation('inbox_v2');

  if (!line_items || line_items.length === 0) return null;

  return (
    <div
      className="rounded-xl border border-border bg-card overflow-hidden shadow-sm max-w-[320px]"
      data-testid="checkout-cart"
    >
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <ShoppingBag className="h-4 w-4 text-muted-foreground" />
        <span className="font-semibold text-sm text-foreground">
          {t('checkout_cart.your_cart')}
        </span>
      </div>

      <div className="divide-y divide-border">
        {line_items.map((item, index) => (
          <LineItem key={index} item={item} currency={currency} />
        ))}
      </div>

      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
        <span className="font-semibold text-sm text-foreground">{t('checkout_cart.total')}</span>
        <span className="font-semibold text-sm text-foreground">
          {formatCurrency(total_amount, currency)}
        </span>
      </div>

      <div className="px-4 pb-4">
        <a
          href={checkout_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full bg-foreground text-white rounded-lg py-2.5 font-medium text-sm hover:bg-foreground/90 transition-colors"
          data-testid="checkout-cart-button"
        >
          {/* TODO(temp-demo): PayPo branch is temporary for a demo — remove after demo. */}
          {is_paypo ? (
            <>
              <span>{t('checkout_cart.pay_with')}</span>
              <PayPoLogo className="h-5 w-auto" />
            </>
          ) : (
            <>
              <span>{checkout_label}</span>
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </a>
      </div>
    </div>
  );
};
